import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "shared/Avatar/Avatar";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { RootState, AppDispatch } from "redux/store";
import {
  getCurrentUser,
  updateUserAPI,
  updateUserWithImageAPI,
} from "../../redux/reducers/UserSlice/UserSlice";
import successHandler from "../../utils/helpers/SuccessHandler";
import { UpdateUserSchema } from "../../utils/formSchema";
import FormField from "../../shared/FormField/FormField";
import __countryListing from "../../data/jsons/__countryListing.json";
import PhoneNumberInput from "shared/PhoneNumberInput/PhoneNumberInput";
import moment from "moment";
import { toast } from "react-toastify";
import { captureError } from "utils/sentry";

const AccountInfo = ({ className = "" }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser, loading } = useSelector(
    (state: RootState) => state.user
  );
  const [phoneValidation, setPhoneValidation] = useState({
    isValid: true,
    message: "",
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      first_name: currentUser?.first_name || "",
      last_name: currentUser?.last_name || "",
      email: currentUser?.email || "",
      date_of_birth:
        (currentUser &&
          moment(currentUser.date_of_birth).format("YYYY-MM-DD")) ||
        "",
      phone: currentUser?.phone || "",
      gender: currentUser?.gender || "",
      country_code: currentUser?.country_code || "",
      facebook_url: currentUser?.facebook_url || "",
      linkedin_url: currentUser?.linkedin_url || "",
      work_place: currentUser?.work_place || "",
      about_me: currentUser?.about_me || "",
      live_in: currentUser?.live_in || "",
      speak_language: currentUser?.speak_language || "",
      payment_info: currentUser?.payment_info || "",
      payment_email: currentUser?.payment_email || "",
      instagram_url: currentUser?.instagram_url || "",
      country_id: currentUser?.country_id || "",
    },
    validationSchema: UpdateUserSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      dispatch(updateUserAPI({ user: values }))
        .unwrap()
        .then((response) => {
          if (response) {
            toast.success("Profile updated successfully!");
            setSubmitError(null);
            dispatch(getCurrentUser());
            formik.resetForm({ values }); // ← fixes dirty state bug too
          }
        })
        .catch((error: any) => {
          const msg = error?.message || "Failed to update profile. Please try again.";
          setSubmitError(msg);
          captureError(error, { stage: "account_info_update", userId: currentUser?.id });
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  useEffect(() => {
  if (currentUser) {
    formik.resetForm({
      values: {
        first_name: currentUser.first_name || "",
        last_name: currentUser.last_name || "",
        email: currentUser.email || "",
        date_of_birth: currentUser?.date_of_birth
          ? moment(currentUser.date_of_birth).format("YYYY-MM-DD")
          : "",
        phone: currentUser.phone || "",
        gender: currentUser.gender || "",
        country_code: currentUser.country_code || "",
        facebook_url: currentUser.facebook_url || "",
        linkedin_url: currentUser.linkedin_url || "",
        work_place: currentUser.work_place || "",
        about_me: currentUser.about_me || "",
        live_in: currentUser.live_in || "",
        speak_language: currentUser.speak_language || "",
        payment_info: currentUser.payment_info || "",
        payment_email: currentUser.payment_email || "",
        instagram_url: currentUser.instagram_url || "",
        country_id: currentUser.country_id || "",
      },
    });
  }
}, [currentUser]);

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.warning("File size should not exceed 5MB.");
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.warning("Please upload an image file (JPG, PNG, GIF).");
        return;
      }

      setUploadingImage(true);
      const formData = new FormData();
      formData.append("user[profile_image]", file);

      try {
        dispatch(updateUserWithImageAPI(formData))
          .unwrap()
          .then((response) => {
            if (response) {
              dispatch(getCurrentUser());
              successHandler("Profile image updated successfully");
            }
          })
          .catch((error: any) => {
  toast.error("Failed to update photo. Please try again.");
  captureError(error, { stage: "profile_image_upload", userId: currentUser?.id });
})
          .finally(() => {
            setUploadingImage(false);
          });
      } catch (error) {
        console.error("Error updating user profile image: ", error);
        setUploadingImage(false);
      }
    }
  };

  // Disable form fields based on verification status
  // For hosts: require both email and host verification
  // For students: only require email verification
  const isDisabled = currentUser?.is_host
    ? !currentUser?.email_verified || !currentUser?.host_verified
    : !currentUser?.email_verified;

  return (
    <div className={`${className}`}>
      {/* Profile Image Section */}
      <div className="mb-8 sm:mb-10">
        <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
          {/* Avatar with Upload */}
          <div className="relative group">
            <div className="relative rounded-full overflow-hidden">
              <Avatar
                sizeClass="w-24 h-24 sm:w-32 sm:h-32"
                hasChecked={true}
                imgUrl={currentUser?.image}
                userName={currentUser?.first_name}
              />

              {/* Overlay on hover */}
              {!isDisabled && (
                <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer">
                  {uploadingImage ? (
                    <div className="flex flex-col items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="mt-2 text-xs font-medium">
                        Uploading...
                      </span>
                    </div>
                  ) : (
                    <>
                      <svg
                        className="w-8 h-8 sm:w-10 sm:h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="mt-2 text-xs font-medium">
                        Change Photo
                      </span>
                    </>
                  )}
                </div>
              )}

              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isDisabled || uploadingImage}
                className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              />
            </div>

            {/* Verified Badge */}
            {currentUser?.email_verified && (
              <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 z-10">
                <div className="relative">
                  <svg
                    className="w-7 h-7 sm:w-8 sm:h-8 drop-shadow-md"
                    viewBox="0 0 122.88 116.87"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <polygon
                      fill="#10a64a"
                      points="61.37 8.24 80.43 0 90.88 17.79 111.15 22.32 109.15 42.85 122.88 58.43 109.2 73.87 111.15 94.55 91 99 80.43 116.87 61.51 108.62 42.45 116.87 32 99.08 11.73 94.55 13.73 74.01 0 58.43 13.68 42.99 11.73 22.32 31.88 17.87 42.45 0 61.37 8.24 61.37 8.24"
                    />
                    <path
                      fill="#fff"
                      d="M37.92,65c-6.07-6.53,3.25-16.26,10-10.1,2.38,2.17,5.84,5.34,8.24,7.49L74.66,39.66C81.1,33,91.27,42.78,84.91,49.48L61.67,77.2a7.13,7.13,0,0,1-9.9.44C47.83,73.89,42.05,68.5,37.92,65Z"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1 min-w-0">
            <div className="space-y-2">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
                {currentUser?.first_name} {currentUser?.last_name}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                {currentUser?.email}
              </p>
              {currentUser?.phone && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {currentUser.phone}
                </p>
              )}
            </div>

            {/* Upload Info */}
            <div className="mt-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-300 font-medium">
                    Profile Photo Tips
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                    Use a clear photo of your face. Max size: 5MB. Formats: JPG,
                    PNG, GIF
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="relative mb-8 sm:mb-10">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200 dark:border-neutral-700"></div>
        </div>
        <div className="relative flex justify-start">
          <span className="pr-3 bg-white dark:bg-neutral-900 text-sm font-medium text-gray-500 dark:text-gray-400">
            Personal Information
          </span>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={formik.handleSubmit} className="space-y-6 sm:space-y-8">
        {/* Basic Information Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            label="* First Name"
            name="first_name"
            placeholder="John"
            type="text"
            disabled={isDisabled}
            value={formik.values.first_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.first_name && formik.errors.first_name}
          />

          <FormField
            label="* Last Name"
            name="last_name"
            placeholder="Doe"
            type="text"
            disabled={isDisabled}
            value={formik.values.last_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.last_name && formik.errors.last_name}
          />

          <FormField
            label="* Email"
            name="email"
            placeholder="john.doe@example.com"
            type="email"
            disabled={true}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email}
          />

          <div>
            <PhoneNumberInput
              label="Phone / WhatsApp"
              name="phone"
              placeholder="+1 (123) 456-7890"
              disabled={isDisabled}
              value={{
                phone: formik.values.phone,
                country_code: formik.values.country_code,
              }}
              onChange={(value: {
                phone: string;
                country_code: string;
                valid_phone_number: string;
                isValid: boolean;
                validationMessage: string;
              }) => {
                formik.setFieldValue("phone", value.phone);
                formik.setFieldValue("country_code", value.country_code);
                formik.setFieldValue(
                  "valid_phone_number",
                  value.valid_phone_number
                );
                setPhoneValidation({
                  isValid: value.isValid,
                  message: value.validationMessage,
                });
                if (value.isValid) {
                  formik.setFieldError("valid_phone_number", undefined);
                }
              }}
            />
            {(!phoneValidation.isValid ||
              (formik.touched.country_code && formik.errors.country_code)) && (
              <p className="mt-1.5 text-xs sm:text-sm text-red-600 dark:text-red-400 font-medium">
                {phoneValidation.isValid
                  ? typeof formik.errors.country_code === "string"
                    ? formik.errors.country_code
                    : "Invalid country code"
                  : phoneValidation.message}
              </p>
            )}
          </div>

          <FormField
            label="Date of Birth"
            name="date_of_birth"
            placeholder="MM/DD/YYYY"
            type="date"
            disabled={!currentUser?.email_verified}
            value={
              formik.values.date_of_birth
                ? moment(formik.values.date_of_birth).isValid()
                  ? moment(formik.values.date_of_birth)
                  : null
                : null
            }
            onChange={(value) => formik.setFieldValue("date_of_birth", value)}
            onBlur={formik.handleBlur}
            error={formik.touched.date_of_birth && formik.errors.date_of_birth}
          />

          <FormField
            label="Gender"
            name="gender"
            placeholder="Select gender"
            type="select"
            disabled={isDisabled}
            value={formik.values.gender || ""}
            onChange={(e) => {
              formik.setFieldValue("gender", e.target.value);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.gender && formik.errors.gender}
            options={[
              { value: "", label: "Select gender" },
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
              { value: "other", label: "Other" },
            ]}
          />

          <FormField
            label="Work Place"
            name="work_place"
            placeholder="Your workplace"
            type="text"
            disabled={isDisabled}
            value={formik.values.work_place}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.work_place && formik.errors.work_place}
          />

          <FormField
            label="Nationality"
            name="live_in"
            placeholder="Select country"
            type="select"
            disabled={isDisabled}
            value={formik.values.live_in}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.live_in && formik.errors.live_in}
            options={[
              { value: "", label: "Select country" },
              ...__countryListing.map((country) => ({
                value: country.name,
                label: country.name,
              })),
            ]}
          />

          <FormField
            label="Identification Number"
            name="country_id"
            placeholder="Country ID or Passport"
            type="text"
            disabled={isDisabled}
            value={formik.values.country_id}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.country_id && formik.errors.country_id}
          />

          <FormField
            label="Languages Spoken"
            name="speak_language"
            placeholder="e.g., English, Spanish"
            type="text"
            disabled={isDisabled}
            value={formik.values.speak_language}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.speak_language && formik.errors.speak_language
            }
          />

          <FormField
            label="PayPal Email"
            name="payment_email"
            placeholder="paypal@example.com"
            type="text"
            disabled={isDisabled}
            value={formik.values.payment_email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.payment_email && formik.errors.payment_email}
          />
        </div>

        {/* About Me Section */}
        <div>
          <FormField
            label="About Me"
            name="about_me"
            placeholder="Tell us about yourself, your interests, and what makes you unique..."
            type="textarea"
            disabled={isDisabled}
            value={formik.values.about_me}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.about_me && formik.errors.about_me}
          />
          <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Write a brief introduction about yourself (minimum 50 characters)
          </p>
        </div>

        {/* Inline error — paste just above the Action Buttons div */}
{submitError && (
  <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" strokeWidth="2"/><line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/><line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/>
    </svg>
    <p className="text-sm font-medium text-red-800 dark:text-red-300 flex-1">{submitError}</p>
    <button onClick={() => setSubmitError(null)} className="text-red-400 hover:text-red-600">✕</button>
  </div>
)}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-6 border-t border-gray-200 dark:border-neutral-700">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-xs sm:text-sm">
              {formik.dirty ? "You have unsaved changes" : "All changes saved"}
            </span>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            {formik.dirty && (
              <button
                type="button"
                onClick={() => formik.resetForm()}
                disabled={formik.isSubmitting}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
            )}
            <ButtonPrimary
              type="submit"
              disabled={
                formik.isSubmitting ||
                isDisabled ||
                !formik.isValid ||
                !formik.dirty
              }
              loading={formik.isSubmitting}
              className="w-full sm:w-auto px-8"
            >
              {formik.isSubmitting ? "Saving..." : "Save Changes"}
            </ButtonPrimary>
          </div>
        </div>

        {/* Disabled State Message */}
        {isDisabled && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  Account Not Fully Verified
                </p>
                <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                  {!currentUser?.email_verified
                    ? "Please verify your email to edit your profile information."
                    : "Your host profile is pending admin approval. You'll be able to edit once approved."}
                </p>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default AccountInfo;
